'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'pl' | 'en' | 'de' | 'it' | 'fr' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
  translationsLoaded: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Default English translations - loaded immediately to prevent flash
const defaultEnglishTranslations: any = {
  nav: {
    home: 'Home',
    products: 'Products',
    ledLights: 'LED lights',
    aboutUs: 'About us',
  },
  cart: {
    cart: 'Cart',
    shoppingCart: 'Shopping Cart',
    empty: 'Your cart is empty',
    addedToCart: 'Added to cart!',
    addedToCartMessage: '{{name}} added to your cart',
    clearCart: 'Clear Cart',
    checkout: 'Checkout',
    total: 'Total',
  },
  footer: {
    shop: 'Shop',
    allProducts: 'All Products',
    newArrivals: 'New Arrivals',
    bestSellers: 'Best Sellers',
    sale: 'Sale',
    customerService: 'Customer Service',
    contactUs: 'Contact Us',
    shippingInfo: 'Shipping Info',
    returnsExchanges: 'Returns & Exchanges',
    faq: 'FAQ',
    contact: 'Contact',
    rightsReserved: 'All rights reserved',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
  },
  common: {
    pln: 'PLN',
    back: 'Back',
    next: 'Next',
    error: 'Error',
  },
  products: {
    title: 'All Products',
    subtitle: 'Browse our complete collection of premium products',
    categories: 'Categories',
    allProducts: 'All Products',
    noCategories: 'No categories available',
    noProducts: 'No Products Found',
    noCategoryProducts: 'There are no products in this category yet.',
    emptyStore: 'Our store is currently empty. Check back soon!',
    viewAll: 'View All Products',
    filter: 'Filter',
    filterBy: 'Filter by Category',
    showing: 'Showing',
    categoryResults: '{{count}} products found',
    loadError: 'Failed to load products. Please try again.',
  },
  banner: {
    freeShipping: 'Free shipping from 400 PLN',
    freeShippingPrefix: 'Free shipping from ',
    freeShippingSuffix: ' PLN',
    guarantee: '1 year guarantee',
  },
  product: {
    addToCart: 'Add to Cart',
    quantity: 'Quantity',
    inCart: 'in cart',
    outOfStock: 'Out of Stock',
    unavailable: 'Unavailable',
    noImage: 'No Image',
    notFound: 'Product not found',
    backToHome: 'Back to Home',
    gross: 'gross',
    net: 'net',
    tabs: {
      description: 'Description',
      specification: 'Specification',
      applications: 'Applications',
      documents: 'Documents',
      tests: 'Tests',
    },
    noSpecifications: 'No specifications available',
    noApplications: 'No applications available',
    noDocuments: 'No documents available',
    noTests: 'No tests available',
  },
  contact: {
    title: 'Contact Us',
    subtitle: "We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
    form: {
      title: 'Send us a Message',
      name: 'Name',
      namePlaceholder: 'Your name',
      email: 'Email',
      emailPlaceholder: 'your.email@example.com',
      subject: 'Subject',
      subjectPlaceholder: 'What is this regarding?',
      message: 'Message',
      messagePlaceholder: 'Your message here...',
      submit: 'Send Message',
      success: "Thank you! Your message has been sent successfully. We'll get back to you soon.",
      error: {
        required: 'Please fill in all required fields',
        invalidEmail: 'Please enter a valid email address',
      },
    },
    info: {
      title: 'Contact Information',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      hours: 'Business Hours',
    },
  },
  aboutUs: {
    title: 'About Us',
    subtitle: 'Learn more about Luxelo and our mission to provide premium products',
    mission: {
      title: 'Our Mission',
      description: 'At Luxelo, we are dedicated to offering high-quality products that enhance your lifestyle. Our mission is to provide exceptional value through carefully curated products, outstanding customer service, and innovative solutions.',
    },
    values: {
      title: 'Our Values',
      quality: {
        title: 'Quality First',
        description: 'We source only the finest products and ensure rigorous quality standards in everything we offer.',
      },
      innovation: {
        title: 'Innovation',
        description: 'We stay ahead of trends and continuously seek new ways to improve our products and services.',
      },
      customer: {
        title: 'Customer Focus',
        description: 'Your satisfaction is our priority. We listen to your feedback and strive to exceed expectations.',
      },
      sustainability: {
        title: 'Sustainability',
        description: 'We are committed to environmentally responsible practices and sustainable business operations.',
      },
    },
    story: {
      title: 'Our Story',
      description: 'Luxelo was founded with a vision to bring premium products to customers worldwide. Since our inception, we have grown into a trusted brand known for quality, reliability, and exceptional customer service. Our team is passionate about what we do and committed to helping you find the perfect products for your needs.',
    },
    team: {
      title: 'Our Team',
      description: 'Behind Luxelo is a dedicated team of professionals who work tirelessly to ensure you have the best shopping experience. From product selection to customer support, every team member is committed to excellence.',
    },
  },
  returns: {
    title: 'Returns & Exchanges',
    subtitle: 'Our return policy ensures your satisfaction with every purchase',
    important: 'You have 30 days from the date of purchase to return or exchange items.',
    policy: {
      title: 'Return Policy',
      description: "We want you to be completely satisfied with your purchase. If you're not happy with your order, you can return it within 30 days of delivery for a full refund or exchange.",
    },
    conditions: {
      title: 'Return Conditions',
      item1: 'Items must be unused, unworn, and in their original packaging',
      item2: 'All tags and labels must be attached',
      item3: 'Items must be in the same condition as when received',
      item4: 'Proof of purchase (receipt or order number) is required',
      item5: 'Some items may be excluded from returns (e.g., personalized items)',
    },
    process: {
      title: 'Return Process',
      step1: {
        title: 'Step 1: Contact Us',
        description: 'Email us at returns@luxelo.com or contact our customer service team with your order number.',
      },
      step2: {
        title: 'Step 2: Get Authorization',
        description: "We'll provide you with a return authorization number and return instructions.",
      },
      step3: {
        title: 'Step 3: Package & Ship',
        description: 'Pack your items securely and ship them back using the provided return label or instructions.',
      },
      step4: {
        title: 'Step 4: Receive Refund',
        description: "Once we receive and inspect your return, we'll process your refund within 5-7 business days.",
      },
    },
    refund: {
      title: 'Refunds',
      description: 'Refunds will be issued to the original payment method. Please allow 5-7 business days for the refund to appear in your account. Shipping costs are non-refundable unless the return is due to our error.',
    },
    exchanges: {
      title: 'Exchanges',
      description: "If you need a different size or color, we'll be happy to exchange your item. Exchanges are subject to product availability. Please contact us to initiate an exchange.",
    },
    contact: {
      title: 'Need Help?',
      description: 'If you have any questions about returns or exchanges, please contact us at returns@luxelo.com or call +48 123 456 789.',
    },
  },
  shipping: {
    title: 'Shipping Information',
    subtitle: 'Fast and reliable shipping options to get your products to you',
    options: {
      title: 'Shipping Options',
      description: 'We offer multiple shipping methods to suit your needs. Choose the option that works best for you.',
    },
    inpost: {
      title: 'InPost Parcel Lockers',
      description: 'Ship to an InPost parcel locker near you for convenient pickup at any time.',
      advantage1: '24/7 access to your package',
      advantage2: 'Thousands of locations across Poland',
      advantage3: 'Easy and secure pickup process',
    },
    courier: {
      title: 'Courier Delivery',
      description: 'Fast door-to-door delivery for your convenience.',
      advantage1: 'Direct delivery to your address',
      advantage2: 'Signature required for security',
    },
    costs: {
      title: 'Shipping Costs',
      table: {
        method: 'Shipping Method',
        price: 'Price',
        delivery: 'Delivery Time',
        inpost: 'InPost Parcel Locker',
        inpostPrice: '9.99 PLN',
        inpostDelivery: '2-3 business days',
        courier: 'Courier Delivery',
        courierPrice: '14.99 PLN',
        courierDelivery: '1-2 business days',
        free: 'Free Shipping',
        freePrice: 'Free',
        freeDelivery: '3-5 business days (orders over 200 PLN)',
      },
    },
    tracking: {
      title: 'Order Tracking',
      description: "Once your order ships, you'll receive a tracking number via email. Use this number to track your package's journey from our warehouse to your door.",
    },
    international: {
      title: 'International Shipping',
      description: 'We currently ship within Poland. For international orders, please contact us at shipping@luxelo.com for availability and pricing.',
    },
    questions: {
      title: 'Shipping Questions?',
      description: 'If you have any questions about shipping, delivery times, or tracking your order, please contact our customer service team at shipping@luxelo.com or call +48 123 456 789.',
    },
  },
  faq: {
    title: 'Frequently Asked Questions',
    subtitle: 'Find answers to common questions about our products and services',
    notFound: "Can't find what you're looking for? Contact us and we'll be happy to help!",
    questions: {
      order: {
        question: 'How do I place an order?',
        answer: "Placing an order is easy! Simply browse our products, add items to your cart, and proceed to checkout. You'll need to provide your shipping information and payment details. Once your order is confirmed, you'll receive an email confirmation with your order number.",
      },
      payment: {
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit cards (Visa, Mastercard, American Express) and debit cards through our secure Stripe payment system. All payments are processed securely and encrypted.',
      },
      shipping: {
        question: 'How long does shipping take?',
        answer: 'Shipping times vary depending on the method you choose:\n- InPost Parcel Lockers: 2-3 business days\n- Courier Delivery: 1-2 business days\n- Free Shipping: 3-5 business days (for orders over 200 PLN)\nOrders are typically processed within 1-2 business days.',
      },
      returns: {
        question: 'Can I return or exchange my order?',
        answer: 'Yes! You have 30 days from the date of purchase to return or exchange items. Items must be unused and in their original packaging. Please contact us at returns@luxelo.com to initiate a return.',
      },
      warranty: {
        question: 'Do your products come with a warranty?',
        answer: "Yes, all our products come with a manufacturer's warranty. Warranty terms vary by product, so please check the product description for specific warranty information. If you have any questions, contact our customer service team.",
      },
      account: {
        question: 'Do I need to create an account to shop?',
        answer: 'No, you can shop as a guest. However, creating an account allows you to track your orders, save your shipping information, and access exclusive offers.',
      },
      product: {
        question: 'Are your products authentic?',
        answer: 'Absolutely! We only sell authentic products from verified suppliers. We guarantee the authenticity of all items in our store. If you have any concerns, please contact us immediately.',
      },
      contact: {
        question: 'How can I contact customer service?',
        answer: 'You can reach us via email at contact@luxelo.de, call us at +48 123 456 789, or use the contact form on our website. Our customer service team is available Monday-Friday, 9:00 AM - 6:00 PM.',
      },
    },
  },
  checkout: {
    title: 'Checkout',
    steps: {
      contact: 'Contact Information',
      shipping: 'Shipping Details',
      invoice: 'VAT Invoice',
      summary: 'Summary',
    },
    contact: {
      title: 'Contact Information',
      name: 'Full Name',
      namePlaceholder: 'John Doe',
      email: 'Email',
      emailPlaceholder: 'john@example.com',
      phone: 'Phone',
      phonePlaceholder: '+48 123 456 789',
    },
    shipping: {
      title: 'Shipping Details',
      method: 'Shipping Method',
      selectMethod: 'Select shipping method',
      inpost: 'InPost Parcel Locker',
      courier: 'Delivery to Address',
      selectPaczkomat: 'Select paczkomat',
      address: {
        title: 'Delivery Address',
        name: 'Full Name',
        namePlaceholder: 'John Doe',
        companyName: 'Company Name (optional)',
        companyNamePlaceholder: 'Company Name',
        phone: 'Phone',
        phonePlaceholder: '+48 123 456 789',
        street: 'Street',
        streetPlaceholder: '123 Main St',
        city: 'City',
        cityPlaceholder: 'Warsaw',
        postCode: 'Postal Code',
        postCodePlaceholder: '00-000',
      },
    },
    invoice: {
      title: 'VAT Invoice',
      needInvoice: 'I need a VAT invoice',
      companyName: 'Company Name',
      companyNamePlaceholder: 'Company Name',
      taxId: 'Tax ID (NIP)',
      taxIdPlaceholder: '1234567890',
      address: 'Company Address',
      addressPlaceholder: '123 Main St, 00-000 Warsaw',
    },
    summary: {
      title: 'Order Summary',
      subtotal: 'Subtotal',
      shipping: 'Shipping Cost',
      vat: 'VAT',
      total: 'Total',
      continueToPayment: 'Continue to Secure Payment',
      redirecting: 'Redirecting to secure payment...',
      securePaymentNote: 'You will be redirected to Stripe Checkout to complete your payment securely',
      freeShipping: 'Free shipping!',
      freeShippingNote: 'Free shipping from {{threshold}} PLN',
      calculating: 'Calculating...',
      vatIncluded: 'incl. VAT (23%)',
      selectShipping: 'Select a shipping method to see the total',
    },
    errors: {
      selectPaczkomat: 'Please select a paczkomat',
      selectShippingMethod: 'Please select a shipping method',
      fillAddress: 'Please fill in all address fields',
      deliveryName: 'Please enter recipient name',
      onlyOutOfStock: 'Your cart contains only out-of-stock items. Please remove them before checkout.',
      name: 'Name must be at least 2 characters',
      email: 'Invalid email address',
      phone: 'Phone number is too short',
      companyName: 'Company name is required for VAT invoice',
      taxId: 'Tax ID is required for VAT invoice',
      invoiceAddress: 'Company address is required for VAT invoice',
      deliveryPhone: 'Please enter phone number',
      submit: 'Failed to create checkout session. Please try again.',
      validation: 'Please fill in all required fields',
    },
    orderSummary: {
      title: 'Order Summary',
      outOfStock: 'Out of Stock',
      remove: 'Remove',
    },
  },
  homepage: {
    hero: {
      badge: 'Premium Quality',
      title: 'Discover Premium Products That Transform Your Life',
      subtitle: 'Experience excellence with our carefully curated selection of high-quality products designed to enhance your lifestyle.',
      shopNow: 'Shop Now',
      learnMore: 'Learn More',
    },
    features: {
      shipping: {
        title: 'Fast Shipping',
        description: 'Quick and reliable delivery to your doorstep or nearest InPost locker',
      },
      quality: {
        title: 'Premium Quality',
        description: 'All products are carefully selected and guaranteed authentic',
      },
      support: {
        title: '24/7 Support',
        description: 'Our customer service team is always here to help you',
      },
      satisfaction: {
        title: '100% Satisfaction',
        description: '30-day return policy ensures your complete satisfaction',
      },
    },
    whyChoose: {
      title: 'Why Choose Luxelo?',
      subtitle: "We're committed to providing you with the best shopping experience and exceptional products that exceed your expectations.",
      premium: {
        title: 'Premium Selection',
        description: 'Every product in our collection is handpicked for quality, durability, and design. We partner with trusted manufacturers to ensure you receive only the best.',
      },
      care: {
        title: 'Customer Care',
        description: "Your satisfaction is our priority. From browsing to delivery and beyond, we're here to ensure your shopping experience is seamless and enjoyable.",
      },
    },
    products: {
      title: 'Our Featured Products',
      subtitle: 'Explore our collection of carefully selected products designed to meet your needs and exceed your expectations.',
      empty: 'No products available at the moment.',
    },
    cta: {
      title: 'Ready to Get Started?',
      subtitle: 'Have questions? Our team is here to help you find the perfect products for your needs.',
      contact: 'Contact Us',
      faq: 'View FAQ',
    },
  },
  paczkomat: {
    selected: 'Selected Paczkomat:',
    change: 'Change Paczkomat',
    select: 'Select InPost Paczkomat',
    modalTitle: 'Select InPost Paczkomat',
    searchPlaceholder: 'Search: city, city + street, postal code or paczkomat code',
    findNearby: 'Find nearby',
    searchExamples: 'Examples: Warsaw, Warsaw Main Street, 00-001, WAW010',
    searching: 'Searching...',
    typeToSearch: 'Type at least 3 characters to search',
    noResults: 'No results found. Try another search.',
    searchHint: 'Search by location or click "Find nearby" to see nearby paczkomats',
    confirmSelection: 'Confirm selection:',
    loading: 'Loading InPost Geowidget...',
    error: {
      loadFailed: 'Failed to load InPost Geowidget. Please try again.',
      apiKeyMissing: 'Error: NEXT_PUBLIC_INPOST_API_KEY is not configured.',
      apiKeyHint: 'Add NEXT_PUBLIC_INPOST_API_KEY to your .env.local file.',
      geolocationNotSupported: 'Geolocation is not supported by your browser',
      noNearbyFound: 'No nearby paczkomats found. Try searching by name.',
      loadNearbyFailed: 'Failed to load nearby paczkomats',
      locationError: 'Cannot get your location: {{message}}',
      searchFailed: 'Search failed. Please try again.',
      noResultsFound: 'No paczkomats found. Try: city name, city + street, postal code or paczkomat code.',
    },
    found: 'Found {{count}} paczkomats',
    foundNearby: 'Found {{count}} paczkomats nearby',
    yourLocation: '📍 Your location',
    note: 'Note',
  },
  orderConfirmation: {
    title: 'Order Confirmed!',
    thankYou: 'Thank you for your purchase. Your order has been received and is being processed.',
    orderId: 'Order Number:',
    total: 'Total:',
    emailConfirmation: 'You will receive an email confirmation shortly with your order details.',
    continueShopping: 'Continue Shopping',
  },
};

const translations: Record<Language, any> = {
  pl: {},
  en: defaultEnglishTranslations, // Preload English immediately
  de: {},
  it: {},
  fr: {},
  es: {},
};

// Load translations dynamically
const loadTranslations = async (lang: Language): Promise<any> => {
  try {
    const response = await fetch(`/translations/${lang}.json`);
    if (!response.ok) {
      throw new Error(`Failed to load translations for ${lang}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error loading translations for ${lang}:`, error);
    // Fallback to English if translation fails
    if (lang !== 'en') {
      return loadTranslations('en');
    }
    return defaultEnglishTranslations;
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en');
  const [translationsLoaded, setTranslationsLoaded] = useState(false); // Start as false to show loader

  useEffect(() => {
    // Load language from localStorage or browser preference
    const savedLanguage = localStorage.getItem('language') as Language;
    const browserLanguage = navigator.language.split('-')[0] as Language;
    const initialLanguage = savedLanguage || (['pl', 'en', 'de', 'it', 'fr', 'es'].includes(browserLanguage) ? browserLanguage : 'en');
    
    // If English, translations are already loaded (preloaded)
    if (initialLanguage === 'en') {
      setLanguageState(initialLanguage);
      // Small delay to ensure smooth transition
      setTimeout(() => {
        setTranslationsLoaded(true);
      }, 100);
      return;
    }

    // Load translations for non-English languages
    setLanguageState(initialLanguage);
    setTranslationsLoaded(false);
    
    loadTranslations(initialLanguage).then((trans) => {
      translations[initialLanguage] = trans;
      setTranslationsLoaded(true);
    }).catch(() => {
      // If loading fails, use English as fallback
      translations[initialLanguage] = defaultEnglishTranslations;
      setLanguageState('en');
      setTranslationsLoaded(true);
    });
  }, []);

  const setLanguage = async (lang: Language) => {
    if (!translations[lang] || Object.keys(translations[lang]).length === 0) {
      setTranslationsLoaded(false);
      const trans = await loadTranslations(lang);
      translations[lang] = trans;
      setTranslationsLoaded(true);
    }
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string, params?: Record<string, string>): string => {
    const keys = key.split('.');
    let value: any = translations[language];

    // If current language not loaded, fallback to English
    if (!value || Object.keys(value).length === 0) {
      value = translations['en'];
    }

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = translations['en'];
        for (const k2 of keys) {
          if (value && typeof value === 'object' && k2 in value) {
            value = value[k2];
          } else {
            return key; // Return key if translation not found
          }
        }
        break;
      }
    }

    if (typeof value !== 'string') {
      return key;
    }

    // Replace parameters
    if (params) {
      return value.replace(/\{\{(\w+)\}\}/g, (match: string, paramKey: string) => {
        return params[paramKey] || match;
      });
    }

    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translationsLoaded }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

